import {CodeEditorInput, Field, MapInput} from 'apstra-ui-common';
import {filter, isEqual, keys, map, omit, some, transform} from 'lodash';
import {observer} from 'mobx-react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Input} from 'semantic-ui-react';

import {ADDITIONAL_PROPERTIES_FIELD_NAME} from '../processorPropertiesRenderers';

export const AdditionalPropertiesEditor = observer(({
  value, onChange, processorDefinition,
  serviceRegistryProperties, knownPythonExpressionVariables,
  onError,
  ...props
}) => {
  const [internalValue, setInternalValue] = useState(value);
  const prevValueRef = useRef(value);

  const onValueChange = useCallback((newValue) => {
    setInternalValue(newValue);
    const forbiddenKeys = [
      ...keys(processorDefinition.schema.properties),
      ...keys(serviceRegistryProperties),
      ADDITIONAL_PROPERTIES_FIELD_NAME
    ];
    const invalidKeys = filter(forbiddenKeys, (key) => key in newValue);
    if (some(invalidKeys)) {
      onError?.(props.name, map(invalidKeys, (key) => ({
        message: `${key} key can not be used`,
        key,
      })));
      newValue = omit(newValue, invalidKeys);
      prevValueRef.current = newValue;
      onChange?.(newValue);
    } else {
      prevValueRef.current = newValue;
      onChange?.(newValue);
      onError?.(props.name, null);
    }
  }, [onChange, onError, processorDefinition.schema.properties, props.name, serviceRegistryProperties]);

  useEffect(() => {
    if (!isEqual(value, prevValueRef.current)) {
      setInternalValue(value);
    }
  }, [value]);

  const keyErrors = useMemo(() => transform(props.errors, (acc, item) => {
    const error = item[props.name];
    if (!error) return;
    (acc[error.key] ??= []).push(error.message);
  }, {}), [props.errors, props.name]);

  return (
    <MapInput
      onChange={onValueChange}
      value={internalValue}
      {...props}
    >
      {({key, value, index, errors, setEntryKey, setEntryValue}) =>
        <>
          <Field
            key='key'
            errors={keyErrors[key]}
            width={4}
          >
            <Input
              placeholder='Key'
              disabled={props.disabled}
              value={key}
              onChange={(e) => setEntryKey(index, e.target.value)}
            />
          </Field>
          <CodeEditorInput
            key='value'
            mode='python-expression'
            enableCompletion
            completerParams={{knownVariables: knownPythonExpressionVariables}}
            value={value}
            fieldProps={{width: 12}}
            placeholder='Expression'
            disabled={props.disabled}
            errors={errors}
            onChange={(value) => setEntryValue(index, value)}
          />
        </>
      }
    </MapInput>
  );
});
